import { Mesh2d } from "../../buffers";
import { Webgl2Component, MeasurementComponent } from "../../webgl2-component";
import { Webgl2 } from "../../webgl2";
import { Webgl2dRenderer } from "../webgl2d-renderer";

export class MousePicking {
  private _texture: WebGLTexture;
  private _fb: WebGLFramebuffer;
  private _data = new Uint8ClampedArray(4);

  private _prepared = false;

  constructor(private readonly _renderer: Webgl2dRenderer) {}

  public prepare() {
    if (this._prepared) return;

    const gl = Webgl2.gl;

    // Create a texture to render to
    this._texture = gl.createTexture() as WebGLTexture;
    gl.bindTexture(gl.TEXTURE_2D, this._texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    // Create and bind the framebuffer
    this._fb = gl.createFramebuffer() as WebGLFramebuffer;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this._fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this._texture, 0);

    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this._prepared = true;
  }

  public getShapeId(e: MouseEvent | TouchEvent) {
    const prevViewport = Webgl2.camera.projection;
    Webgl2.camera.save();

    const gl = Webgl2.gl;

    gl.bindFramebuffer(gl.FRAMEBUFFER, this._fb);

    // Setup block
    gl.viewport(0, 0, 1, 1);
    Webgl2.camera.reset(1, 1);
    Webgl2.clear();
    const globalPixelPosition = this._renderer.camera.getGlobalMousePosition(e);
    Webgl2.camera.setTranslation(globalPixelPosition);
    Webgl2.camera.setScale(this._renderer.camera.scale);

    // Render block
    this._renderPickingComponent(this._renderer.chessGridComponent);

    for (const component of this._renderer.components) {
      this._renderPickingComponent(component);
    }

    if (this._renderer.eventsManager.checkForTool(this._renderer.tools.cropTool)) {
      this._renderPickingComponent(this._renderer.cropSelectionComponent);
    } else if (this._renderer.eventsManager.checkForTool(this._renderer.tools.canvasResizeTool)) {
      this._renderPickingComponent(this._renderer.canvasResizeSelectionComponent);
    } else {
      if (this._renderer.selectedComponent instanceof MeasurementComponent) {
        this._renderPickingComponent(this._renderer.measurementSelectionComponent);
      } else {
        this._renderPickingComponent(this._renderer.selectionComponent);
      }
    }

    // Read block
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, this._data);
    const id = this._data[0] + (this._data[1] << 8) + (this._data[2] << 16) + (this._data[3] << 24);

    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    Webgl2.setViewport(prevViewport);
    Webgl2.camera.restore();

    return id;
  }

  private _renderPickingComponent(component: Webgl2Component<any, any>) {
    if (
      component.style.display === "none" ||
      component.style.pointerEvents === "none" ||
      !component.getRenderData({} as any)
    )
      return;

    const mousePickingMaterial = Webgl2.materials.picking;

    mousePickingMaterial.setUniforms({
      u_model: component.getModelMatrix(),
      u_shapeId: component.u_id,
    });

    Webgl2.renderMesh(Mesh2d.meshesMap.rectIndexedMesh, mousePickingMaterial);

    component.forEachChildren((child) => {
      this._renderPickingComponent(child);
    });
  }
}

export default MousePicking;
